import { useEffect } from "react";
import { useLocation, NavLink } from "react-router-dom";
import PropTypes from "prop-types";
import {
  List,
  Divider,
  Link,
  Icon,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Tooltip,
  ListItem,
  Box,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import SidenavCollapse from "examples/Sidenav/SidenavCollapse";
import SidenavRoot from "examples/Sidenav/SidenavRoot";
import sidenavLogoLabel from "examples/Sidenav/styles/sidenav";
import useMediaQuery from "@mui/material/useMediaQuery";

import {
  useMaterialUIController,
  setMiniSidenav,
  setTransparentSidenav,
  setWhiteSidenav,
} from "context";
import EmployeeAvatar from "components/EmployeeAvatar";
import { useTheme } from "@emotion/react";
import { getSafeColor } from "services/utils";

function Sidenav({ color, brand, brandName, routes, employeeName, photo, ...rest }) {
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, transparentSidenav, whiteSidenav, darkMode, sidenavColor } = controller;
  const location = useLocation();
  const collapseName = location.pathname.replace("/", "");
  const theme = useTheme();
  let textColor = "white";
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("xl"));

  if (transparentSidenav || (whiteSidenav && !darkMode)) {
    textColor = "dark";
  } else if (whiteSidenav && darkMode) {
    textColor = "inherit";
  }

  const closeSidenav = () => setMiniSidenav(dispatch, true);

  useEffect(() => {
    function handleMiniSidenav() {
      setMiniSidenav(dispatch, window.innerWidth < 1200);
      setTransparentSidenav(dispatch, window.innerWidth < 1200 ? false : transparentSidenav);
      setWhiteSidenav(dispatch, window.innerWidth < 1200 ? false : whiteSidenav);
    }

    window.addEventListener("resize", handleMiniSidenav);
    handleMiniSidenav();
    return () => window.removeEventListener("resize", handleMiniSidenav);
  }, [dispatch, location]);

  // Group routes by category
  const groupedRoutes = routes.reduce((acc, route) => {
    if (route.type === "collapse" && route.category) {
      if (!acc[route.category]) {
        acc[route.category] = [];
      }
      acc[route.category].push(route);
    } else {
      if (!acc.uncategorized) {
        acc.uncategorized = [];
      }
      acc.uncategorized.push(route);
    }
    return acc;
  }, {});

  // Render routes with categories
  const signOutRoute = groupedRoutes.uncategorized?.find((route) => route.key === "sign-out");

  // Filter out "Sign Out" from `uncategorized`
  const updatedUncategorizedRoutes = groupedRoutes.uncategorized?.filter(
    (route) => route.key !== "sign-out"
  );

  const renderGroupedRoutes = Object.keys(groupedRoutes).map((category) => {
    const theme = useTheme();
    const { sideNav } = theme.palette;
    const { rgba } = theme.functions;

    // 🧠 HR Semantic Icon Mapping
    // This looks for keywords in your category names and assigns an icon
    const getCategoryIcon = (cat) => {
      if (cat.includes("Manpower") || cat.includes("تخطيط القوى العاملة")) return "science";
      if (cat.includes("Competences") || cat.includes("الكفاءات")) return "insights";
      if (cat.includes("Scorecards") || cat.includes("مؤشرات الأداء")) return "assessment";
      return "work_outline"; // Default professional icon
    };
    let routesInCategory = groupedRoutes[category];

    if (category === "uncategorized") {
      routesInCategory = updatedUncategorizedRoutes;

      return routesInCategory.map((route) => {
        const { type, name, icon, title, noCollapse, key, href, route: path } = route;

        // Get theme colors for the titles and dividers
        const theme = useTheme();
        const { sideNav } = theme.palette;
        const { rgba } = theme.functions;

        if (type === "collapse") {
          const collapseElement = (
            <SidenavCollapse
              name={name}
              icon={icon}
              active={key === collapseName}
              noCollapse={noCollapse}
            />
          );

          return href ? (
            <Link
              href={href}
              key={key}
              target="_blank"
              rel="noreferrer"
              sx={{ textDecoration: "none" }}
            >
              {collapseElement}
            </Link>
          ) : (
            <NavLink key={key} to={path} style={{ textDecoration: "none" }}>
              {collapseElement}
            </NavLink>
          );
        } else if (type === "title") {
          return (
            <MDTypography
              key={key}
              display="block"
              variant="caption"
              fontWeight="bold"
              textTransform="uppercase"
              sx={{
                // UPGRADED LOOK: Matches the category headers
                color: sideNav.contrastText,
                opacity: 0.5,
                letterSpacing: "0.1em",
                fontSize: "0.65rem",
                pl: 3,
                mt: 3,
                mb: 1,
              }}
            >
              {title}
            </MDTypography>
          );
        } else if (type === "divider") {
          return (
            <Divider
              key={key}
              sx={{
                // UPGRADED LOOK: Subtle "Ghost" line
                borderColor: rgba(sideNav.contrastText, 0.1),
                my: 1.5,
                mx: 2,
              }}
            />
          );
        }
        return null;
      });
    }

    // 🎀 Professional Category Accordion
    return (
      <Accordion
        key={category}
        disableGutters
        elevation={0}
        sx={{
          background: "transparent",
          "&:before": { display: "none" },
          mb: 0.25,
        }}
      >
        <AccordionSummary
          expandIcon={
            <ExpandMoreIcon
              sx={{ color: sideNav.contrastText, fontSize: "0.8rem", opacity: 0.4 }}
            />
          }
          sx={{
            minHeight: "40px !important",
            padding: "0 10px",
            borderRadius: "6px",
            "& .MuiAccordionSummary-content": { margin: "0", gap: 1.5, alignItems: "center" },
            "&:hover": { backgroundColor: rgba(sideNav.contrastText, 0.07) },
          }}
        >
          {/* Category Icon - Makes it Human Friendly */}
          <Icon
            sx={{
              color: sideNav.contrastText,
              fontSize: "1.1rem !important",
              opacity: 0.72,
            }}
          >
            {getCategoryIcon(category)}
          </Icon>

          {!miniSidenav && (
            <Typography
              variant="button"
              fontWeight="bold"
              sx={{
                textTransform: "uppercase",
                fontSize: "0.68rem",
                letterSpacing: "0.05em",
                color: sideNav.contrastText,
                opacity: 0.74,
              }}
            >
              {category}
            </Typography>
          )}
        </AccordionSummary>

        <AccordionDetails sx={{ p: 0, pl: 1.25, pt: 0.25, position: "relative" }}>
          {/* Visual Guide Line (Indents children nicely) */}
          <Box
            sx={{
              position: "absolute",
              insetInlineStart: "18px",
              top: 0,
              bottom: 10,
              width: "1px",
              backgroundColor: rgba(sideNav.contrastText, 0.1),
            }}
          />

          {routesInCategory.map((route) => (
            <NavLink key={route.key} to={route.route} style={{ textDecoration: "none" }}>
              <SidenavCollapse
                name={route.name}
                icon={route.icon}
                active={route.key === collapseName}
              />
            </NavLink>
          ))}
        </AccordionDetails>
      </Accordion>
    );
  });
  return (
    <SidenavRoot
      {...rest}
      variant={isMobile ? "temporary" : "permanent"}
      open={!miniSidenav}
      onClose={closeSidenav}
      ModalProps={{
        keepMounted: true,
      }}
      ownerState={{ transparentSidenav, whiteSidenav, miniSidenav, darkMode }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", height: "100vh", overflow: "hidden" }}>
        {/* 🏢 1. REFINED LOGO SECTION */}
        <MDBox
          px={3}
          py={2}
          textAlign="center"
          display="flex"
          justifyContent="center"
          alignItems="center"
          sx={(theme) => ({
            minHeight: 72,
            borderBottom: `1px solid ${theme.functions.rgba(
              getSafeColor(theme.palette.sideNav.contrastText),
              0.1
            )}`,
          })}
        >
          <NavLink to="/" style={{ textDecoration: "none", display: "inline-block" }}>
            <MDBox
              sx={(theme) => ({
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "6px",
                backgroundColor: "transparent",
                transition: "background-color 160ms ease",
                "&:hover": {
                  backgroundColor: theme.functions.rgba(theme.palette.sideNav.contrastText, 0.07),
                },
              })}
            >
              <MDBox
                component="img"
                src={brand ? `data:image/png;base64,${brand}` : "/fallback-logo.png"}
                alt="Brand"
                sx={{
                  width: "100%",
                  maxWidth: miniSidenav ? "30px" : "85px",
                  height: "auto",
                  filter: darkMode ? "brightness(0) invert(1)" : "none",
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  objectFit: "contain",
                  borderRadius: "6px",
                }}
              />
            </MDBox>
          </NavLink>
        </MDBox>

        {/* 2. SCROLLABLE NAVIGATION AREA */}
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            px: 1,
            pt: 1.25,
            pb: 2,
            // --------------------------------------------------
            // 🎨 MODERN SCROLLBAR STYLING START
            // --------------------------------------------------
            scrollbarWidth: "thin", // Firefox
            scrollbarColor: `${theme.functions.rgba(
              theme.palette.sideNav?.contrastText || "#000",
              0.2
            )} transparent`, // Firefox

            "&::-webkit-scrollbar": {
              width: "6px", // Thin scrollbar
            },
            "&::-webkit-scrollbar-track": {
              background: "transparent", // Invisible track
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: theme.functions.rgba(
                theme.palette.sideNav?.contrastText || "#000",
                0.2
              ), // Subtle color matching text
              borderRadius: "20px", // Rounded corners
              border: "transparent",
            },
            "&::-webkit-scrollbar-thumb:hover": {
              backgroundColor: theme.functions.rgba(
                theme.palette.sideNav?.contrastText || "#000",
                0.5
              ), // Darker on hover
            },
            // --------------------------------------------------
            // 🎨 MODERN SCROLLBAR STYLING END
            // --------------------------------------------------
          }}
        >
          <List sx={{ pt: 0 }}>{renderGroupedRoutes}</List>
        </Box>

        {/* 🚪 3. STICKY SIGN OUT (Professional Footer) */}
        <Box
          sx={(theme) => ({
            p: 2,
            mt: "auto",
            borderTop: `1px solid ${theme.functions.rgba(
              getSafeColor(theme.palette.sideNav.contrastText),
              0.1
            )}`,
            backgroundColor: theme.functions.rgba(getSafeColor(theme.palette.sideNav.main), 0.96),
            backdropFilter: "none",
          })}
        >
          {signOutRoute && (
            <NavLink
              key={signOutRoute.key}
              to={signOutRoute.route}
              style={{ textDecoration: "none" }}
            >
              <SidenavCollapse
                name={signOutRoute.name}
                icon={signOutRoute.icon || "logout"}
                active={signOutRoute.key === collapseName}
              />
            </NavLink>
          )}
        </Box>
      </Box>
    </SidenavRoot>
  );
}

Sidenav.defaultProps = {
  color: "info",
  brand: "",
};

Sidenav.propTypes = {
  color: PropTypes.oneOf(["primary", "secondary", "info", "success", "warning", "error", "dark"]),
  brand: PropTypes.string,
  brandName: PropTypes.string.isRequired,
  routes: PropTypes.arrayOf(PropTypes.object).isRequired,
  employeeName: PropTypes.string.isRequired,
  photo: PropTypes.any,
};

export default Sidenav;
